/**
 * How wire values READ in the console.
 *
 * The panel is used by the business team, so every code that crosses the wire
 * — a severity, a breaker state, an alarm kind, a lifecycle status — is shown
 * as words. The wire value itself is never changed here (what is sent back is
 * still the code); only its presentation is. An unknown value degrades to a
 * readable sentence-case phrase rather than disappearing, so a vocabulary the
 * services grow later still shows up.
 */

export function humanise(code: string | null | undefined): string {
  if (code === null || code === undefined) return '—';
  const words = code.replace(/[_-]+/g, ' ').trim();
  if (words === '') return '—';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const SEVERITY: Record<string, string> = {
  ncii_suspected: 'Suspected intimate imagery',
  explicit_unmatched: 'Explicit, face not matched',
  unassessed: 'Not yet assessed',
  benign_copy: 'Benign copy',
  likely_not_subject: 'Likely not this person',
};

export function severityLabel(code: string | null | undefined): string {
  if (code === null || code === undefined) return '—';
  return SEVERITY[code] ?? humanise(code);
}

export function decisionLabel(code: string | null | undefined): string {
  return humanise(code);
}

const BREAKER: Record<string, string> = {
  closed: 'Healthy',
  open: 'Tripped',
  half_open: 'Recovering',
};

export function breakerLabel(state: unknown): string {
  if (typeof state !== 'string') return '—';
  return BREAKER[state] ?? humanise(state);
}

const ALARM: Record<string, string> = {
  no_successful_calls_24h: 'No successful calls in 24 hours',
  breaker_open: 'Breaker tripped',
  budget_exceeded: 'Daily budget exhausted',
  provider_disabled: 'Provider switched off',
  high_error_rate: 'High error rate',
};

export function alarmLabel(kind: string): string {
  return ALARM[kind] ?? humanise(kind);
}

const KIND: Record<string, string> = {
  leak: 'Leak',
  deepfake_wave: 'Deepfake wave',
  platform_incident: 'Platform incident',
  other: 'Other',
};

export function kindLabel(kind: string): string {
  return KIND[kind] ?? humanise(kind);
}

const STATUS: Record<string, string> = {
  machine_triaged: 'Awaiting the person',
};

export function statusLabel(status: unknown): string {
  if (typeof status !== 'string') return '—';
  return STATUS[status] ?? humanise(status);
}

/** The first block of a uuid; anything that is not one is short already. */
export function shortId(id: string): string {
  return /^[0-9a-f]{8}-/i.test(id) ? `${id.slice(0, 8)}…` : id;
}

export function fmtWhen(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Money for READING: two decimals, rounded half-up on the string itself, so
 * the wire value is never parsed into a float. Anything that is not a plain
 * decimal is shown as it came.
 */
export function fmtMoney(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (!/^-?\d+(\.\d+)?$/.test(value)) return value;
  return `$${roundDecimalString(value, 2)}`;
}

function roundDecimalString(value: string, places: number): string {
  const negative = value.startsWith('-');
  const [intPart = '0', fracPart = ''] = value.replace(/^-/, '').split('.');
  const digits = `${fracPart}${'0'.repeat(places + 1)}`.slice(0, places + 1);
  const kept = digits.slice(0, places);
  const carry = Number(digits.charAt(places)) >= 5 ? 1n : 0n;
  const scaled = (BigInt(`${intPart}${kept}`) + carry).toString().padStart(places + 1, '0');
  const whole = scaled.slice(0, scaled.length - places);
  const fraction = scaled.slice(scaled.length - places);
  return `${negative ? '-' : ''}${whole}.${fraction}`;
}

/**
 * The services' rolling reporting window for call counts, success rate and
 * latency (`window_hours` on the health payload — 1 hour on dev). It rides on
 * the column headers so a "0 calls" beside "3 OK (24h)" is not a contradiction.
 */
export function windowLabel(hours: unknown): string {
  return typeof hours === 'number' && Number.isFinite(hours) ? `${String(hours)}h` : '';
}

export function hoursText(hours: unknown): string {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) return 'the reporting window';
  return hours === 1 ? 'the last hour' : `the last ${String(hours)} hours`;
}
